/**
 * SettingsAI — Settings → AI tab. Connect the workspace's AI providers (BYOK).
 *
 * Admin-only (operator decision 2026-07-16): the tab is filtered to admins in
 * SettingsShell and the body re-guards.
 *
 * Multi-provider model (overlay 03d81dfc): an admin can store keys for ALL three
 * providers (Anthropic · OpenAI · Gemini); keys are retained per provider. Exactly
 * ONE provider is ACTIVE at a time — the active provider is the DEFAULT that
 * powers "Build with AI". Switching the active provider to one that already has a
 * stored key needs no key re-entry (a bare PUT { provider } activates it).
 *
 * No fake interactivity: connect / activate / save-model / remove each await the
 * typed ai-api op; success only after resolve, real error state on failure.
 * Reads never expose the stored key — only a masked keyHint.
 */
import { useEffect, useMemo, useState } from "react";
import { alpha, Btn, Card, Dropdown, Field, Icon, Input, T } from "@v0y4ge/jrni-ui";
import { PageHeader } from "../../shell/chrome";
import { isAdmin } from "../../shell/access";
import { toast } from "../../shell/toast";
import SettingsShell from "./SettingsShell";
import { DataSourceStrip } from "../../shell/opSource";
import { friendlyDateTime } from "../../lib/datetime";
import {
  AI_PROVIDERS, providerDef, getAiProviderSettings, putAiProviderSettings,
  deleteAiProviderSettings, testAiProviderConnection, listAiProviderModels, aiErrorCopy,
  type AiProvider, type AiProviderSettings, type AiSettingsResult, type AiProviderDef,
} from "../ai/ai-api";
import { useT, t as i18nT } from "../../lib/i18n";

const OTHER = "__other__";
const lbl: React.CSSProperties = { display: "block", fontSize: T.text.bodySm.fontSize, fontWeight: 600, color: "var(--jrni-color-neutral-1)", marginBottom: T.space[2] };

/** Brand mark per provider — a small rounded tile with the provider's emblem. */
function ProviderLogo({ id, size = 30 }: { id: AiProvider; size?: number }) {
  // ui-config: every hex below is a THIRD-PARTY brand mark (Anthropic, OpenAI,
  // Gemini's gradient) plus the glyph fill that sits on it. These are vendor
  // identity, not theme values — no --jrni token can or should express them, and
  // they must NOT follow the tenant palette or invert in dark.
  const box: React.CSSProperties = { width: size, height: size, borderRadius: T.radius.md, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 };
  const g = Math.round(size * 0.62);
  if (id === "anthropic") {
    return (
      <span style={{ ...box, background: "#141413" }} aria-hidden="true">
        <svg width={g} height={g} viewBox="0 0 24 24" fill="none"><path d="M6.7 4h3.1l5.9 16h-3.2l-1.2-3.4H6.1L4.9 20H1.8L6.7 4Zm.3 9.7h3.3L8.65 8.2 7 13.7Z" fill="#fff"/><path d="M14.1 4h3.15L23.2 20h-3.15L14.1 4Z" fill="#fff"/></svg>
      </span>
    );
  }
  if (id === "openai") {
    return (
      <span style={{ ...box, background: "#000" }} aria-hidden="true">
        <svg width={g} height={g} viewBox="0 0 24 24" fill="#fff"><path d="M21.6 9.9a5.4 5.4 0 0 0-.46-4.43 5.46 5.46 0 0 0-5.88-2.62A5.4 5.4 0 0 0 11.2 1a5.46 5.46 0 0 0-5.2 3.78 5.4 5.4 0 0 0-3.6 2.62 5.46 5.46 0 0 0 .67 6.4 5.4 5.4 0 0 0 .46 4.43 5.46 5.46 0 0 0 5.88 2.62A5.4 5.4 0 0 0 12.8 23a5.46 5.46 0 0 0 5.2-3.78 5.4 5.4 0 0 0 3.6-2.62 5.46 5.46 0 0 0-.67-6.4l-.33-.3.33.9Zm-8.13 11.3a4.05 4.05 0 0 1-2.6-.94l.13-.07 4.32-2.5a.7.7 0 0 0 .35-.6v-6.1l1.83 1.06v5.05a4.06 4.06 0 0 1-4.06 4.09Zm-8.73-3.73a4.05 4.05 0 0 1-.48-2.72l.13.08 4.32 2.5a.7.7 0 0 0 .71 0l5.28-3.05v2.11l-4.37 2.53a4.06 4.06 0 0 1-5.54-1.48ZM3.6 8.3a4.05 4.05 0 0 1 2.12-1.78v5.14a.7.7 0 0 0 .35.6l5.27 3.05-1.83 1.06-4.32-2.5a4.06 4.06 0 0 1-1.86-5.06l.27-.51Zm14.98 3.48-5.28-3.06 1.83-1.05 4.32 2.49a4.06 4.06 0 0 1-.62 7.33v-5.14a.7.7 0 0 0-.37-.61l.1.04Zm1.82-2.74-.13-.08-4.32-2.5a.7.7 0 0 0-.71 0L10.65 9.5V7.4l4.37-2.53a4.06 4.06 0 0 1 6.02 4.2l.36.03-.98-.77ZM9.66 12.96 7.83 11.9V6.85a4.06 4.06 0 0 1 6.66-3.12l-.13.07-4.32 2.5a.7.7 0 0 0-.35.6l-.03 6.06Zm.99-2.14L13 9.45l2.35 1.36v2.72L13 14.9l-2.35-1.36v-2.72Z"/></svg>
      </span>
    );
  }
  return (
    <span style={{ ...box, background: "#fff", border: `1px solid ${T.border}` }} aria-hidden="true">
      <svg width={g} height={g} viewBox="0 0 24 24"><defs><linearGradient id="gem-g" x1="0" y1="0" x2="24" y2="24"><stop offset="0" stopColor="#4796E3"/><stop offset="0.5" stopColor="#9177C7"/><stop offset="1" stopColor="#D96570"/></linearGradient></defs><path d="M12 2c.4 5.3 4.7 9.6 10 10-5.3.4-9.6 4.7-10 10-.4-5.3-4.7-9.6-10-10 5.3-.4 9.6-4.7 10-10Z" fill="url(#gem-g)"/></svg>
    </span>
  );
}

export default function SettingsAI() {
  const t = useT();
  const admin = isAdmin();
  const [settings, setSettings] = useState<AiProviderSettings | null>(null);
  const [configuredProviders, setConfiguredProviders] = useState<AiProvider[]>([]);
  const [loading, setLoading] = useState(true);

  const [provider, setProvider] = useState<AiProvider>("anthropic"); // the provider the form below edits
  const [apiKey, setApiKey] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [modelSel, setModelSel] = useState<string>("");   // "" = provider default; OTHER = custom
  const [customModel, setCustomModel] = useState("");
  const [extras, setExtras] = useState<Record<string, string>>({});

  const [testResult, setTestResult] = useState<{ ok: boolean; text: string } | null>(null);
  const [liveModels, setLiveModels] = useState<Partial<Record<AiProvider, string[]>>>({});
  const [modelsSyncedAt, setModelsSyncedAt] = useState<string | null>(null);
  const [syncingModels, setSyncingModels] = useState(false);
  const [editingKey, setEditingKey] = useState(false); // true = key step; false = model step
  const [validating, setValidating] = useState(false);
  const [savingModel, setSavingModel] = useState(false);
  const [activating, setActivating] = useState<AiProvider | null>(null);
  const [removing, setRemoving] = useState<AiProvider | "all" | null>(null);

  const def: AiProviderDef = useMemo(() => providerDef(provider), [provider]);
  const activeProvider: AiProvider | null = settings?.status === "configured" ? (settings.provider ?? null) : null;
  const isConfigured = (p: AiProvider) => configuredProviders.includes(p);
  const providerConfigured = isConfigured(provider);
  const modelOptions = (liveModels[provider] && liveModels[provider]!.length) ? liveModels[provider]! : def.models;

  const fetchModels = async (prov: AiProvider) => {
    setSyncingModels(true);
    try {
      const r = await listAiProviderModels(prov);
      if (Array.isArray(r.models) && r.models.length) {
        setLiveModels((p) => ({ ...p, [prov]: r.models }));
        setModelsSyncedAt(r.fetchedAt || null);
      }
    } catch {
      /* keep the built-in fallback list */
    } finally {
      setSyncingModels(false);
    }
  };

  /** Apply a settings result. `select` picks the form provider from the active
   *  one (initial load / activation); otherwise the current selection is kept. */
  const apply = (r: AiSettingsResult, select: boolean) => {
    setSettings(r.settings);
    setConfiguredProviders(r.configuredProviders || []);
    const target = select && r.settings.status === "configured" && r.settings.provider ? r.settings.provider : provider;
    if (select && r.settings.status === "configured" && r.settings.provider) setProvider(r.settings.provider);
    // Sync the model form to the active provider's stored values when it's the target.
    if (r.settings.status === "configured" && r.settings.provider === target) {
      const pd = providerDef(target);
      if (r.settings.model && !pd.models.includes(r.settings.model)) { setModelSel(OTHER); setCustomModel(r.settings.model); }
      else { setModelSel(r.settings.model || ""); setCustomModel(""); }
      setExtras({ ...(r.settings.baseUrl ? { baseUrl: r.settings.baseUrl } : {}) });
    }
    setEditingKey(!(r.configuredProviders || []).includes(target));
  };

  const refresh = async (select: boolean) => {
    const r = await getAiProviderSettings();
    apply(r, select);
    return r;
  };

  useEffect(() => {
    let live = true;
    getAiProviderSettings()
      .then((r) => { if (live) apply(r, true); })
      .catch((e) => { if (live) toast({ tone: "warn", title: t("settings.aI.couldnTLoadAiSettings", "Couldn't load AI settings"), subtitle: aiErrorCopy(e).detail }); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync the live model list for the selected provider whenever it changes and a key is stored.
  useEffect(() => {
    if (isConfigured(provider)) void fetchModels(provider);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider, configuredProviders.join(",")]);

  const resolvedModel = modelSel === OTHER ? customModel.trim() : (modelSel || modelOptions[0]);

  /** Select a provider to configure/edit in the form below. */
  const selectProviderForm = (p: AiProvider, forceKeyStep = false) => {
    setProvider(p);
    setApiKey("");
    setTestResult(null);
    const conf = isConfigured(p);
    if (conf && p === activeProvider) {
      const pd = providerDef(p);
      if (settings?.model && !pd.models.includes(settings.model)) { setModelSel(OTHER); setCustomModel(settings.model); }
      else { setModelSel(settings?.model || ""); setCustomModel(""); }
      setExtras({ ...(settings?.baseUrl ? { baseUrl: settings.baseUrl } : {}) });
    } else {
      setModelSel(""); setCustomModel(""); setExtras({});
    }
    setEditingKey(forceKeyStep || !conf);
  };

  // Connect / replace a key: validate the typed key inline (nothing persisted),
  // then PUT it — which also ACTIVATES this provider. A configured provider may
  // be re-activated without a new key (bare PUT), handled by activate().
  const validateKey = async () => {
    const typed = apiKey.trim();
    if (!providerConfigured && typed.length < 20) {
      toast({ tone: "warn", title: t("settings.aI.enterYourApiKey", "Enter your API key"), subtitle: i18nT("settings.aI.pasteAValidDefNameKeyAtLeast20Characters.icu", { value0: def.name }) });
      return;
    }
    setValidating(true); setTestResult(null);
    try {
      if (typed.length >= 20) {
        const t = await testAiProviderConnection({ provider, apiKey: typed, ...(extras.baseUrl ? { baseUrl: extras.baseUrl } : {}) });
        if (!t.ok) {
          setTestResult({ ok: false, text: aiErrorCopy({ code: t.code === "invalid_key" ? "ai_invalid_credentials" : t.code }).detail });
          return;
        }
      }
      const put = await putAiProviderSettings({
        provider,
        ...(typed ? { apiKey: typed } : {}),
        ...(extras.baseUrl ? { baseUrl: extras.baseUrl } : {}),
      });
      apply(put, true);
      setApiKey("");
      setTestResult(null);
      toast({ tone: "success", title: i18nT("settings.aI.defNameConnected.icu", { value0: def.name }), subtitle: i18nT("settings.aI.defNameIsNowTheActiveAiLoadingModels.icu", { value0: def.name }) });
      void fetchModels(provider);
    } catch (e) {
      setTestResult({ ok: false, text: aiErrorCopy(e).detail });
    } finally {
      setValidating(false);
    }
  };

  // Save the chosen model for the (already configured) provider. A PUT overwrites
  // model/baseUrl with what it sends, so we include the current form values; the
  // key is omitted (retained) unless the user just typed a new one.
  const saveModel = async () => {
    if (modelSel === OTHER && !customModel.trim()) { toast({ tone: "warn", title: t("settings.aI.enterAModelId", "Enter a model id") }); return; }
    setSavingModel(true);
    try {
      const put = await putAiProviderSettings({
        provider,
        ...(apiKey.trim().length >= 20 ? { apiKey: apiKey.trim() } : {}),
        model: resolvedModel,
        ...(extras.baseUrl ? { baseUrl: extras.baseUrl } : {}),
      });
      apply(put, true);
      toast({ tone: "success", title: t("settings.aI.aiProviderReady", "AI provider ready"), subtitle: i18nT("settings.aI.defNameResolvedmodelBuildWithAiIsAvailableInEventCreation.icu", { value0: def.name, value1: resolvedModel }) });
    } catch (e) {
      toast({ tone: "danger", title: t("settings.aI.couldnTSaveTheModel", "Couldn't save the model"), subtitle: aiErrorCopy(e).detail });
    } finally {
      setSavingModel(false);
    }
  };

  // Activate a provider that already has a stored key (bare PUT — no re-entry).
  const activate = async (prov: AiProvider) => {
    if (prov === activeProvider || !isConfigured(prov)) return;
    setActivating(prov);
    try {
      const put = await putAiProviderSettings({ provider: prov });
      apply(put, true);
      toast({ tone: "success", title: i18nT("settings.aI.providerdefProvNameIsNowActive.icu", { value0: providerDef(prov).name }), subtitle: t("settings.aI.buildWithAiNowUsesThisProviderByDefault", "Build with AI now uses this provider by default.") });
    } catch (e) {
      toast({ tone: "danger", title: t("settings.aI.couldnTSwitchProvider", "Couldn't switch provider"), subtitle: aiErrorCopy(e).detail });
    } finally {
      setActivating(null);
    }
  };

  const removeProvider = async (prov: AiProvider) => {
    if (!confirm(`Remove the ${providerDef(prov).name} key? ${prov === activeProvider ? "It's the active provider — Build with AI will be unavailable until another is activated." : ""}`)) return;
    setRemoving(prov);
    try {
      await deleteAiProviderSettings(prov);
      await refresh(true);
      toast({ tone: "success", title: i18nT("settings.aI.providerdefProvNameDisconnected.icu", { value0: providerDef(prov).name }), subtitle: t("settings.aI.theStoredKeyWasRemoved", "The stored key was removed.") });
    } catch (e) {
      toast({ tone: "danger", title: t("settings.aI.couldnTDisconnect", "Couldn't disconnect"), subtitle: aiErrorCopy(e).detail });
    } finally {
      setRemoving(null);
    }
  };

  const disconnectAll = async () => {
    if (!confirm("Remove ALL AI provider keys? Build with AI will be unavailable until you connect one again.")) return;
    setRemoving("all");
    try {
      await deleteAiProviderSettings();
      await refresh(true);
      toast({ tone: "success", title: t("settings.aI.allAiProvidersDisconnected", "All AI providers disconnected"), subtitle: t("settings.aI.everyStoredKeyWasRemoved", "Every stored key was removed.") });
    } catch (e) {
      toast({ tone: "danger", title: t("settings.aI.couldnTDisconnect", "Couldn't disconnect"), subtitle: aiErrorCopy(e).detail });
    } finally {
      setRemoving(null);
    }
  };

  if (!admin) {
    return (
      <SettingsShell activeTab="ai">
        <PageHeader title={t("settings.aI.ai", "AI")} subtitle={t("settings.aI.connectTheAiProviderUsedToBuildEvents", "Connect the AI provider used to build events")} />
        <div style={{ padding: T.space[6] }}>
          <Card style={{ padding: T.space[6], textAlign: "center", color: T.soft }}>
            <Icon.lock size={20} />
            <div style={{ marginTop: T.space[2], fontSize: T.text.body.fontSize, fontWeight: 600, color: T.n1 }}>{t("settings.aI.adminsOnly", "Admins only")}</div>
            <div style={{ marginTop: T.space[1], fontSize: T.text.bodySm.fontSize }}>{t("settings.aI.onlyWorkspaceAdministratorsCanConfigureTheAiProvider", "Only workspace administrators can configure the AI provider.")}</div>
          </Card>
        </div>
      </SettingsShell>
    );
  }

  const anyConfigured = configuredProviders.length > 0;

  return (
    <SettingsShell activeTab="ai">
      <PageHeader title={t("settings.aI.ai", "AI")} subtitle={t("settings.aI.connectAiProvidersUsedToBuildEventsStoreKeysForAnyProvider", "Connect AI providers used to build events. Store keys for any provider — one is active at a time. Keys are stored encrypted and billed to your own provider account.")} />
      <DataSourceStrip screen="settings-ai" ops={["getAiProviderSettings", "putAiProviderSettings", "deleteAiProviderSettings", "testAiProviderConnection", "listAiProviderModels"]} />

      <div style={{ padding: `${T.space[4]} ${T.space[6]} ${T.space[10]}`, maxWidth: 760, display: "flex", flexDirection: "column", gap: T.space[4] }}>
        {loading ? (
          <div style={{ padding: T.space[10], border: `1px dashed ${T.n4}`, borderRadius: T.radius.md, textAlign: "center", color: T.soft, fontSize: T.text.body.fontSize }}>{t("settings.aI.loadingAiSettings", "Loading AI settings…")}</div>
        ) : (
          <>
            {/* Status banner — the ACTIVE provider is the default for Build with AI. */}
            <Card style={{ padding: T.space[4], display: "flex", alignItems: "center", gap: T.space[3], background: activeProvider ? alpha(T.green1, "0c") : T.n6, border: `1px solid ${activeProvider ? alpha(T.green1, "44") : T.border}` }}>
              <span style={{ width: 34, height: 34, borderRadius: T.radius.md, background: activeProvider ? T.green1 : T.n4, color: T.white, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                {activeProvider ? <Icon.check size={17} /> : <Icon.star size={17} />}
              </span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: T.text.body.fontSize, fontWeight: 700, color: T.n1 }}>
                  {activeProvider ? /* TODO(i18n): replace template-literal with t('settings.aI.text', { ... }) */`${providerDef(activeProvider).name} is active` : t('settings.aI.text2')}
                </div>
                <div style={{ fontSize: T.text.bodySm.fontSize, color: T.n2, marginTop: T.space[1] }}>
                  {activeProvider
                    ? <>{t("settings.aI.defaultForBuildWithAiKey", "Default for Build with AI · Key")}{" "}{settings?.keyHint || "…••••"}{settings?.model ? /* TODO(i18n): replace template-literal with t('settings.aI.text4', { ... }) */` · ${settings.model}` : ""}{settings?.updatedAt ? /* TODO(i18n): replace template-literal with t('settings.aI.text5', { ... }) */` · updated ${friendlyDateTime(settings.updatedAt)}` : ""}</>
                    : t('settings.aI.text3')}
                </div>
              </div>
            </Card>

            {/* Providers — store a key for any provider; pick which one is active
                (the active provider is the default that powers Build with AI). */}
            <Card style={{ padding: T.space[4] }}>
              <div style={{ fontSize: T.text.bodySm.fontSize, fontWeight: 700, color: T.n1, marginBottom: T.space[1] }}>{t("settings.aI.providers", "Providers")}</div>
              <div style={{ fontSize: T.text.label.fontSize, color: T.soft, marginBottom: T.space[3], lineHeight: 1.5 }}>{t("settings.aI.connectKeysForAnyOfTheThreeProvidersSelectOneToMake", "Connect keys for any of the three providers. Select one to make it the active default; switching to a provider you've already connected needs no key re-entry.")}</div>
              <div style={{ display: "flex", flexDirection: "column", gap: T.space[2] }}>
                {AI_PROVIDERS.map((p) => {
                  const connected = isConfigured(p.id);
                  const active = activeProvider === p.id;
                  const selected = provider === p.id;
                  const busy = activating === p.id || removing === p.id;
                  return (
                    <div key={p.id}
                      style={{ display: "flex", alignItems: "center", gap: T.space[3], padding: `${T.space[3]} ${T.space[3]}`, borderRadius: T.radius.md, border: `1.5px solid ${active ? T.p1 : selected ? T.p2 : T.border}`, background: active ? T.p4 : T.white }}>
                      {/* Active radio — selectable once the provider has a stored key */}
                      {/* ui-raw: bespoke 18px radio-dot toggle with a connected-vs-not opacity treatment (0.5) distinct from jrni-ui's Radio (16px/1.5px-border/no opacity axis) — swapping would drift the exact circle size/border/dim pixels */}
                      {/* ui-raw: 18px single-choice control — this is a Radio, not a Button. */}
                      <button type="button" aria-label={i18nT("settings.aI.makePNameActive.icu", { value0: p.name })} title={connected ? (active ? t("settings.settingsAI.activeProvider", "Active provider") : t("settings.settingsAI.setAsActive", "Set as active")) : t("settings.settingsAI.connectAKeyFirst", "Connect a key first")}
                        disabled={!connected || active || busy} onClick={() => void activate(p.id)}
                        style={{ width: 18, height: 18, borderRadius: T.radius.pill, border: `2px solid ${active ? T.p1 : T.n4}`, background: "transparent", display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0, cursor: (!connected || active) ? "default" : "pointer", padding: 0, opacity: connected ? 1 : 0.5 }}>
                        {active ? <span style={{ width: 9, height: 9, borderRadius: T.radius.pill, background: T.p1 }} /> : null}
                      </button>
                      <ProviderLogo id={p.id} size={30} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: T.text.body.fontSize, fontWeight: 600, color: T.n1, display: "flex", alignItems: "center", gap: T.space[2] }}>
                          {p.name}
                          {active ? <span style={{ fontSize: T.text.label.fontSize, fontWeight: 800, letterSpacing: 0.4, color: T.p1, background: T.white, border: `1px solid ${T.p2}`, borderRadius: T.radius.sm, padding: `0 ${T.space[2]}` }}>{t("settings.aI.active", "ACTIVE")}</span> : null}
                        </div>
                        <div style={{ fontSize: T.text.label.fontSize, color: T.soft }}>
                          {connected
                            ? (active ? `Active · ${settings?.model || providerDef(p.id).models[0]}` : "Connected · select to activate")
                            : t('settings.aI.text6')}{" "}{busy ? t('settings.aI.text7') : ""}
                        </div>
                      </div>
                      <Btn variant="ghost" size="sm" disabled={busy} onClick={() => selectProviderForm(p.id, !connected)}>{connected ? t("settings.aI.replaceKey", "Replace key") : t("account.details.connect", "Connect")}</Btn>
                      {connected ? <Btn variant="ghost" size="sm" icon={<Icon.logout size={13} />} disabled={busy} onClick={() => void removeProvider(p.id)}>{removing === p.id ? t("settings.settingsAI.disconnecting", "Disconnecting…") : t("account.details.disconnect", "Disconnect")}</Btn> : null}
                    </div>
                  );
                })}
              </div>
            </Card>

            {/* Connect / configure the selected provider — Step 1 key → Step 2 model */}
            <Card style={{ padding: T.space[4], display: "flex", flexDirection: "column", gap: T.space[4] }}>
              <div style={{ fontSize: T.text.bodySm.fontSize, fontWeight: 700, color: T.n1, display: "flex", alignItems: "center", gap: T.space[2] }}>
                <ProviderLogo id={provider} size={22} />
                {providerConfigured ? /* TODO(i18n): replace template-literal with t('settings.aI.text8', { ... }) */`Configure ${def.name}` : /* TODO(i18n): replace template-literal with t('settings.aI.text9', { ... }) */`Connect ${def.name}`}
                {activeProvider === provider ? <span style={{ marginLeft: T.space[2], fontSize: T.text.label.fontSize, fontWeight: 600, color: T.green1 }}>{t("settings.aI.active506018d3", "· active")}</span> : null}
              </div>

              {editingKey ? (
                <>
                  <div style={{ fontSize: T.text.bodySm.fontSize, fontWeight: 600, color: T.n2, display: "inline-flex", alignItems: "center", gap: T.space[2] }}>
                    <span style={{ width: 22, height: 22, borderRadius: T.radius.pill, background: T.p1, color: T.white, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: T.text.label.fontSize, fontWeight: 700 }}>1</span>
                    {providerConfigured ? /* TODO(i18n): replace template-literal with t('settings.aI.text10', { ... }) */`Replace your ${def.name} key` : /* TODO(i18n): replace template-literal with t('settings.aI.text11', { ... }) */`Add your ${def.name} key`}
                  </div>
                  <Field htmlFor="ai-key" label={<>{def.keyLabel} {providerConfigured ? <span style={{ fontWeight: 400, color: T.soft }}>{t("settings.aI.optionalKeepStoredKey", "(optional — keep stored key)")}</span> : null}</>} hint={def.keyHelp}>
                    <div style={{ display: "flex", gap: T.space[2] }}>
                      <Input id="ai-key" type={showKey ? "text" : "password"} value={apiKey} autoComplete="off"
                        onChange={(e) => { setApiKey(e.target.value); setTestResult(null); }}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void validateKey(); } }}
                        placeholder={providerConfigured ? t("settings.settingsAI.storedSettingsProviderProviderSettingsKeyhintKeyEnterANewKeyTo", { value0: settings?.provider === provider ? (settings?.keyHint || "…••••") : "key" }) : def.keyPlaceholder}
                        style={{ flex: 1 }} />
                      <Btn variant="secondary" size="sm" icon={<Icon.eye size={14} />} style={{ height: 40 }} onClick={() => setShowKey((v) => !v)}>{showKey ? t("hubs.editor.hide", "Hide") : t("settings.settingsAI.show", "Show")}</Btn>
                    </div>
                  </Field>

                  {def.extras.filter((ex) => ex.key === "baseUrl").map((ex) => (
                    <Field key={ex.key} htmlFor={`ai-${ex.key}`} label={<>{ex.label} <span style={{ fontWeight: 400, color: T.soft }}>{t("settings.aI.optional", "(optional)")}</span></>}>
                      <Input id={`ai-${ex.key}`} value={extras[ex.key] || ""} onChange={(e) => setExtras((p) => ({ ...p, [ex.key]: e.target.value }))} placeholder={ex.placeholder} />
                    </Field>
                  ))}

                  {testResult && !testResult.ok ? (
                    <div style={{ display: "flex", alignItems: "center", gap: T.space[2], fontSize: T.text.bodySm.fontSize, fontWeight: 600, color: T.red1 }}>
                      <Icon.warn size={15} /> {testResult.text}
                    </div>
                  ) : null}

                  <div style={{ display: "flex", gap: T.space[2], alignItems: "center", paddingTop: T.space[1] }}>
                    <Btn icon={<Icon.check size={14} />} disabled={validating || (!providerConfigured && apiKey.trim().length < 20)} onClick={() => void validateKey()}>
                      {validating ? t("settings.settingsAI.validating", "Validating…") : providerConfigured ? t("settings.settingsAI.saveKey", "Save key") : t("settings.settingsAI.validateConnect", "Validate & connect")}
                    </Btn>
                    {providerConfigured ? <Btn variant="ghost" size="sm" onClick={() => { setEditingKey(false); setApiKey(""); setTestResult(null); }}>{t("common.actions.cancel", "Cancel")}</Btn> : null}
                  </div>
                </>
              ) : (
                <>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: T.space[2] }}>
                    <div style={{ fontSize: T.text.bodySm.fontSize, fontWeight: 600, color: T.green1, display: "inline-flex", alignItems: "center", gap: T.space[2] }}>
                      <Icon.check size={15} /> {t("settings.aI.keyStored", "Key stored")}{" "}{settings?.provider === provider ? /* TODO(i18n): replace template-literal with t('settings.aI.text12', { ... }) */` · ${settings?.keyHint || "…••••"}` : ""}
                    </div>
                    <Btn variant="link" size="sm" icon={<Icon.edit size={12} />} onClick={() => { setEditingKey(true); setApiKey(""); setTestResult(null); }}
                      style={{ padding: 0, height: "auto", fontSize: T.text.label.fontSize, fontWeight: 600, gap: T.space[2] }}>
                      {t("settings.aI.replaceKey", "Replace key")}</Btn>
                  </div>

                  <div>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: T.space[2], marginBottom: T.space[2] }}>
                      <label style={{ ...lbl, marginBottom: 0 }} htmlFor="ai-model">
                        <span style={{ display: "inline-flex", alignItems: "center", gap: T.space[2] }}>
                          <span style={{ width: 22, height: 22, borderRadius: T.radius.pill, background: T.p1, color: T.white, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: T.text.label.fontSize, fontWeight: 700 }}>2</span>
                          {t("settings.aI.chooseAModel", "Choose a model")}</span>
                      </label>
                      <Btn variant="link" size="sm" disabled={syncingModels} icon={<Icon.refresh size={12} />} onClick={() => void fetchModels(provider)}
                        title={t("settings.aI.fetchTheCurrentModelListFromTheProvider", "Fetch the current model list from the provider")}
                        style={{ padding: 0, height: "auto", fontSize: T.text.label.fontSize, fontWeight: 600, gap: T.space[2] }}>
                        {syncingModels ? t("settings.settingsAI.syncing", "Syncing…") : t("settings.settingsAI.refreshFromProvider", "Refresh from provider")}
                      </Btn>
                    </div>
                    <Dropdown
                      value={modelSel}
                      onChange={(v) => setModelSel(v)}
                      aria-label={t("settings.aI.chooseAModel", "Choose a model")}
                      options={[
                        { value: "", label: /* TODO(i18n): replace template-literal with t('settings.aI.text13', { ... }) */`Default (${modelOptions[0]})` },
                        ...modelOptions.map((m) => ({ value: m, label: m })),
                        { value: OTHER, label: t("settings.aI.other", "Other…") },
                      ]}
                    />
                    {modelSel === OTHER ? (
                      <Input value={customModel} onChange={(e) => setCustomModel(e.target.value)} placeholder={t("settings.aI.enterAModelId", "Enter a model id")} style={{ marginTop: T.space[2] }} />
                    ) : null}
                    <div style={{ fontSize: T.text.label.fontSize, color: T.soft, marginTop: T.space[2], lineHeight: 1.45, display: "inline-flex", alignItems: "center", gap: T.space[2] }}>
                      {syncingModels
                        ? <><Icon.refresh size={12} /> {t("settings.aI.loadingTheCurrentModelsFrom", "Loading the current models from")}{" "}{def.name}…</>
                        : (liveModels[provider] && liveModels[provider]!.length
                          ? <><Icon.check size={12} style={{ color: T.green1 }} /> {t("settings.aI.syncedFrom", "Synced from")}{" "}{def.name}{modelsSyncedAt ? ` · ${friendlyDateTime(modelsSyncedAt)}` : ""} {t("settings.aI.theCurrentListForThisKey", "— the current list for this key.")}</>
                          : <><Icon.info size={12} /> {t("settings.aI.showingTheBuiltInListUseRefreshToSyncTheLiveModels", "Showing the built-in list — use Refresh to sync the live models.")}</>)}
                    </div>
                  </div>

                  <div style={{ paddingTop: T.space[1] }}>
                    <Btn icon={<Icon.check size={14} />} disabled={savingModel} onClick={() => void saveModel()}>{savingModel ? t("settings.settingsAI.saving", "Saving…") : t("settings.settingsAI.saveModel", "Save model")}</Btn>
                  </div>
                </>
              )}
            </Card>

            <p style={{ fontSize: T.text.label.fontSize, color: T.soft, lineHeight: 1.55, margin: 0 }}>
              {t("settings.aI.bringYourOwnKeyByokAllAiUsageIsBilledToYour", "Bring your own key (BYOK): all AI usage is billed to your provider account, never to jrni. Keys are encrypted at rest and never returned by the API — only a masked hint is shown.")}</p>
          </>
        )}
      </div>
    </SettingsShell>
  );
}
